<template>
  <section>
    <autocomplete-search
      label-id="services-input"
      placeholder="Search services or filters."
      :filters="allFilters"
      :names="names"
      @filter-unique="filterUniqueService"
      @search="updateSearch"
      @update-filter-selection="updateFilterSelection"
    ></autocomplete-search>
    <service-filter
      :serviceTypes="serviceTypes"
      :locations="locations"
      :resultCount="totalServices"
      :searchTerm="searchTerm"
      @update-filter="updateFilters"
    ></service-filter>
    <div
      class="services__transition-block"
      :class="{ 'slide': slide }"
      @transitionend="manageTransition()"
    >
      <service
        v-for="service in services"
        :key="service.id"
        :contact="service.contact"
        :description="service.description"
        :location="service.location"
        :name="service.name"
        :tags="service.tags"
        :url="service.url"
      ></service>
    </div>
    <div class="services__page-controls">
      <button v-if="prevDisabled" class="button services__previous" disabled>
        <span class="icon icon--arrow"><arrow-svg></arrow-svg></span>
        <span class="services__button-text">Previous</span>
      </button>
      <a
        v-else
        :href="pageLink(-1)"
        class="button services__previous"
        rel="prev"
        @click.prevent="getPage(-1, $event)"
      >
        <span class="icon icon--arrow"><arrow-svg></arrow-svg></span>
        <span class="services__button-text">Previous</span>
      </a>
      <p class="is-small services__pages">Page {{ page + 1 }} of {{ pageCount }}</p>
      <button v-if="nextDisabled" class="button services__next" disabled>
        <span class="services__button-text">Next</span>
        <span class="icon icon--arrow"><arrow-svg></arrow-svg></span>
      </button>
      <a
        v-else
        :href="pageLink(1)"
        class="button services__next"
        rel="next"
        @click.prevent="getPage(1, $event)"
      >
        <span class="services__button-text">Next</span>
        <span class="icon icon--arrow"><arrow-svg></arrow-svg></span>
      </a>
    </div>
  </section>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import { sendGaEvent } from '@/utils';
import AutocompleteSearch from '../AutocompleteSearch.vue';
import Service from './Service.vue';
import ServiceFilter from './ServiceFilter.vue';
import ArrowSvg from '../svg/arrow.vue';

const PAGE_TOTAL = 20;

export default {
  name: 'service-index',
  components: {
    AutocompleteSearch,
    Service,
    ServiceFilter,
    ArrowSvg,
  },
  props: {
    allFilters: Array,
    locations: Object,
    names: Array,
    serviceTypes: Object,
  },
  computed: {
    pageCount() {
      return Math.ceil(this.totalServices / PAGE_TOTAL);
    },
    prevDisabled() {
      return this.page === 0;
    },
    nextDisabled() {
      return this.page + 1 >= this.pageCount;
    },
    selectedLocation: {
      get() {
        return this.$store.state.selectedLocation;
      },
      set(value) {
        this.$store.commit('setSelectedLocation', value);
      },
    },
    selectedServiceType: {
      get() {
        return this.$store.state.selectedServiceType;
      },
      set(value) {
        this.$store.commit('setSelectedServiceType', value);
      },
    },
  },
  data() {
    return {
      page: 0,
      searchTerm: '',
      services: [],
      slide: true,
      slug: null,
      totalServices: 0,
    };
  },
  mounted() {
    // Set event handler to call function when history changes
    window.onpopstate = this.updateFromUrl;
    this.updateFromUrl();
  },
  methods: {
    /**
     * Contruct the query string from the API from selected filters/pages/slug
     */
    buildQueryString() {
      let queryString;

      if (this.slug) {
        queryString = `slug=${this.slug}`;
      } else {
        queryString = [];
        if (this.searchTerm) {
          queryString.push(`search=${this.searchTerm}`);
        }
        if (this.selectedServiceType) {
          queryString.push(`service_type=${this.selectedServiceType}`);
        }
        if (this.selectedLocation) {
          queryString.push(`location=${this.selectedLocation}`);
        }
        queryString.push(`limit=${PAGE_TOTAL}&offset=${(this.page) * PAGE_TOTAL}`);
        queryString = queryString.join('&');
      }

      return `${process.env.VUE_APP_API_URL}services/?${queryString}`;
    },
    /**
     * Filter for a service with the provided slug
     */
    filterUniqueService(payload) {
      this.slug = payload;
      this.selectedServiceType = '';
      this.selectedLocation = '';
      this.resetPage();
      this.updateUrl();
      this.getData();
    },
    /**
     * Get or update data via AJAX call
     */
    getData() {
      // If transition out hasn't started
      // Early exit and wait for @transitionend
      if (!this.slide) {
        this.slide = true;
        return;
      }

      axios.get(this.buildQueryString()).then((response) => {
        this.services = response.data.items;
        this.totalServices = response.data.meta.total_count;
        Vue.nextTick(() => {
          this.slide = false;
          this.slug = null;
        });
      });
    },
    /**
     * Update page and reload data
     */
    getPage(increment, event) {
      this.page += increment;
      this.updateUrl();
      event.currentTarget.blur();
      window.scrollTo(0, 0);
      this.getData();
    },
    /**
     * Fetch new data on transition end only if transitioning out
     */
    manageTransition() {
      if (this.slide) {
        this.getData();
      }
    },
    /**
     * Construct dummy page link for crawling
     */
    pageLink(increment) {
      let newPage;
      if (increment < 0) {
        newPage = this.page;
      } else {
        newPage = this.page + 2;
      }

      const queryString = [];
      if (this.searchTerm) {
        queryString.push(`search=${this.searchTerm}`);
      }
      if (this.selectedServiceType) {
        queryString.push(`service_type=${this.selectedServiceType}`);
      }
      if (this.selectedLocation) {
        queryString.push(`location=${this.selectedLocation}`);
      }
      if (newPage > 1) {
        queryString.push(`page=${this.page + 1 + increment}`);
      }
      if (queryString.length === 0) {
        return window.location.pathname;
      }
      return `?${queryString.join('&')}`;
    },
    /**
     * Reset page number
     */
    resetPage() {
      this.page = 0;
    },
    /**
     * update selected filter, also updating the dropdown in ServiceFilter
     */
    updateFilterSelection(payload) {
      const { field, value } = payload;

      if (field === 'service_type') {
        this.selectedServiceType = value;
      }
      if (field === 'location') {
        this.selectedLocation = value;
      }
      this.updateFilters(payload);
    },
    /**
     * Update selected filters
     */
    updateFilters() {
      this.resetPage();
      this.updateUrl();
      this.getData();
    },
    /**
     * Update data from URL params
     */
    updateFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      this.slug = urlParams.get('slug');

      const search = urlParams.get('search');
      if (search) {
        this.searchTerm = search;
      } else {
        this.searchTerm = '';
      }

      const serviceType = Number.parseInt(urlParams.get('service_type'), 10);
      const location = urlParams.get('location');
      if (serviceType) {
        this.selectedServiceType = serviceType;
      } else {
        this.selectedServiceType = '';
      }
      if (location) {
        this.selectedLocation = location;
      } else {
        this.selectedLocation = '';
      }

      const urlPage = Number.parseInt(urlParams.get('page'), 10);
      if (urlPage && urlPage > 0) {
        this.page = urlPage - 1;
      } else {
        this.page = 0;
      }
      this.getData();
    },
    /**
     * Update searchTerm
     */
    updateSearch(payload) {
      this.searchTerm = payload;
      this.resetPage();
      this.updateUrl();
      this.getData();
      if (payload) { // Don't send event with empty string
        sendGaEvent('Search', 'help', payload);
      }
    },
    /**
     * Update the url with the current state of data, avoiding ? if no params are set
     */
    updateUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      if (this.slug) {
        urlParams.set('slug', this.slug);
      } else {
        urlParams.delete('slug');
      }
      if (this.searchTerm) {
        urlParams.set('search', this.searchTerm);
      } else {
        urlParams.delete('search');
      }

      if (this.selectedServiceType) {
        urlParams.set('service_type', this.selectedServiceType);
      } else {
        urlParams.delete('service_type');
      }
      if (this.selectedLocation) {
        urlParams.set('location', this.selectedLocation);
      } else {
        urlParams.delete('location');
      }

      if (this.page > 0) {
        urlParams.set('page', this.page + 1);
      } else {
        urlParams.delete('page');
      }
      const queryString = urlParams.toString();
      if (queryString) {
        window.history.pushState({}, '', `?${queryString}`);
      } else {
        window.history.pushState({}, '', window.location.pathname);
      }
    },
  },
};
</script>
